import ColorBox from 'components/shared/colorBox';
import { ReactComponent as StatusIcon } from '../../../assets/icons/status-icon.svg';
import { ReactComponent as ImpressionIcon } from '../../../assets/icons/impression.svg';
import { ReactComponent as ModeratorIcon } from '../../../assets/icons/people-grey.svg';
import { ReactComponent as TagsIcon } from '../../../assets/icons/tags-icon.svg';
import { ONE_ON_ONE_COMPLETION_STATUSES } from '../../../constants/statuses';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  IMPRESSION_VALUES,
  RECURRING_TYPES_MAP,
} from '../../../constants/oneOnOne';
import { isArrayEmpty, isUserDeactivated } from '../../../utility/helpers';
import { getPersonFullName } from '../../../utility/uiUtils';
import { formatDate, isDateInPast } from '../../../utility/dateUtils';
import { filterTags } from '../../../utility/tagUtils';
import { PARAMS } from '../../../constants/pages';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';

export const prepareOneOnOnePageFilters = (
  translations,
  tagCategories,
  quickFilters = [],
  allManagers = []
) => {
  const { COMPLETION_STATUS, IMPRESSION, MODERATOR, TAGS } = PARAMS;
  const defaultOption = { id: null, name: translations.all };
  const statuses = ONE_ON_ONE_COMPLETION_STATUSES.map(status => ({
    ...status,
    name: translations.statuses[status.id],
  }));
  const impressions = IMPRESSION_VALUES.map(impression => ({
    ...impression,
    id: impression.value,
    name: impression.value.toString(),
  }));

  return [
    {
      id: MODERATOR,
      name: translations.filters.labels[MODERATOR],
      icon: ModeratorIcon,
      items: allManagers,
    },
    {
      id: COMPLETION_STATUS,
      name: translations.filters.labels.status,
      icon: StatusIcon,
      isSingleSelect: true,
      items: [defaultOption, ...statuses],
    },
    {
      id: IMPRESSION,
      name: translations.filters.labels.impression,
      icon: ImpressionIcon,
      items: impressions,
    },
    {
      id: TAGS,
      name: translations.filters.labels.tags,
      icon: TagsIcon,
      categoryItemsKey: TAGS,
      hasCategoryColorBox: true,
      hasSubmenuHash: true,
      isCategorized: true,
      items: tagCategories,
      categorySearch: filterTags,
    },
    ...(!isArrayEmpty(quickFilters)
      ? quickFilters?.map(filter => {
          return {
            id: filter.name,
            name: filter.name,
            icon: () => {
              return <ColorBox bgColor={filter.color} isSmall />;
            },
            hasParent: true,
            parent: {
              id: TAGS,
            },
            items: filter?.tags,
          };
        }) || {}
      : []),
  ];
};

export const prepareOneOnOneTableHeaderActions = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    rowCellClass: 'actions-cell',
    align: 'right',
    isHeaderAction: true,
    minWidth: 60,
    maxWidth: 145,
  },
];

export const prepareOneOnOneTableHeaders = labels => [
  {
    id: 1,
    title: labels.with,
    rowKey: 'user',
    isHighlighted: true,
    isSortingDisabled: true,
    minWidth: 160,
    maxWidth: '1fr',
  },
  {
    id: 2,
    title: labels.moderator,
    rowKey: 'moderator',
    minWidth: 160,
    maxWidth: '1fr',
    hasConditionalTooltip: true,
    isSortingDisabled: true,
    isHighlighted: true,
  },
  {
    id: 3,
    title: labels.date,
    rowKey: 'date',
    rowCellClass: 'date-cell',
    isSortingDisabled: true,
    maxWidth: '1fr',
  },
  {
    id: 4,
    title: labels.status,
    rowKey: 'status',
    isSortingDisabled: true,
    maxWidth: '1fr',
  },
];

export const prepareOneOnOneTableData = (
  records,
  currentUser,
  accessControl,
  renderUser,
  renderStatus,
  renderActions
) => {
  return records.map(record => {
    const canManage = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_EDIT,
      accessControl,
      currentUser.id,
      record.user.id
    );
    const canDelete = hasScopedAuthPermissionCheck(
      PERMISSION_CODES.ONE_ON_ONE_DELETE,
      accessControl,
      currentUser.id,
      record.user.id
    );
    const detailsUrl = `/1-1/${record.id}/details`;
    const isRequest = !record.meeting_time;
    const isCreatedForCurrentUser = currentUser.id === record.user?.id;
    const isCurrentUserModerator = currentUser.id === record.moderator?.id;
    const isPastMeeting = isDateInPast(record.meeting_time);
    const hasImpression = record.has_impression;
    const isActiveUser = !isUserDeactivated(record.user);
    const hasTasks = !isArrayEmpty(record.tasks);
    const hasPlans = !isArrayEmpty(record.plans);

    return {
      id: record.id,
      actions: renderActions(
        record,
        isCurrentUserModerator,
        isCreatedForCurrentUser,
        hasImpression,
        isRequest,
        isPastMeeting,
        canManage,
        canDelete,
        isActiveUser
      ),
      user: renderUser(
        record.user,
        record.requested && record.meeting_time !== null
      ),
      moderator: record.moderator ? getPersonFullName(record.moderator) : '-',
      status: renderStatus(
        isCreatedForCurrentUser,
        record.meeting_time,
        record.meeting_impression,
        record.previous_impression,
        record.recurring !== RECURRING_TYPES_MAP.DOES_NOT_REPEAT.id,
        hasImpression,
        hasTasks,
        hasPlans
      ),
      date: formatDate(record.meeting_time),
      ...(isRequest
        ? {}
        : {
            rowLink: detailsUrl,
          }),
    };
  });
};
